import { Box, Typography } from '@mui/material';
import { Add, Point } from 'assets';
import { useTheme } from 'contexts/Theme/Theme';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as setup from 'setup';
import { useWindowSize } from 'usehooks-ts';
import FunnelInput from '../FunnelInput/FunnelInput';
import FunnelRetentionArrow from '../FunnelRetentionArrow/FunnelRetentionArrow';
import FunnelZoomButton from '../FunnelZoomButton/FunnelZoomButton';
import IconButton from '../IconButton/IconButton';
import { FunnelRetentionBody, FunnelRetentionContainer } from './FunnelRetention.styles';

export default function FunnelRetention({
    adding,
    connected,
    connecting,
    disableInputs,
    disableZooms,
    step,
    zoomable,
    zoomed,
    onAdd,
    onChange,
    onConnect,
    onZoom,
}: FunnelRetentionProps) {

    const [anchor, setAnchor] = useState({ x: 0, y: 0 });
    const theme = useTheme();
    const windowSize = useWindowSize();

    const { decimals, multiplier } = setup.game.retention;

    const updateAnchor = useCallback((e: any) => {
        setAnchor({
            x: e.pageX + 10,
            y: e.pageY,
        });
    }, []);

    useEffect(() => {
        if (connecting) {
            window.addEventListener('mousemove', updateAnchor);
        }

        return () => {
            window.removeEventListener('mousemove', updateAnchor);
        };
    }, [
        connecting,
        updateAnchor,
    ]);

    const lineCurveness = useMemo(() => {
        const value = (windowSize.width - 1024) / 500;
        return 1.5 * _.clamp(value, 0, 1) + 0.5;
    }, [
        windowSize.width,
    ]);

    return (
        <>
            <Box
                sx={{
                    position: 'absolute',
                    right: -10,
                    top: 34,
                    zIndex: 1
                }}>
                {(connected || connecting) && (
                    <FunnelRetentionArrow
                        end={`funnel-retention-${connecting ? 'anchor' : 'end'}`}
                        endAnchor={{ position: 'right' }}
                        label={{
                            middle: (
                                <FunnelRetentionContainer>
                                    {(zoomable || zoomed) && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                left: -20,
                                                top: 'calc(50% - 15px)'
                                            }}>
                                            <FunnelZoomButton
                                                disabled={disableZooms}
                                                type={zoomable ? 'in' : 'out'}
                                                onClick={type => onZoom?.(step, type)}
                                            />
                                        </Box>
                                    )}
                                    <Typography align="center" variant="montserrat414" sx={{
                                        marginTop: 1
                                    }}>
                                        Retention to step {step.index + (connecting ? 1 : 0)}
                                    </Typography>
                                    <FunnelRetentionBody>
                                        <Typography variant="montserrat412">
                                            MIN
                                        </Typography>
                                        <FunnelInput
                                            decimals={decimals}
                                            disabled={disableInputs}
                                            max={step.retention.max}
                                            multiplier={multiplier}
                                            value={(connected && !zoomable) ? step.retention.min : undefined}
                                            onChange={value => onChange({ min: value })}
                                        />
                                        <Typography variant="montserrat412">
                                            MAX
                                        </Typography>
                                        <FunnelInput
                                            decimals={decimals}
                                            disabled={disableInputs}
                                            max={1}
                                            min={step.retention.min}
                                            multiplier={multiplier}
                                            value={(connected && !zoomable) ? step.retention.max : undefined}
                                            onChange={value => onChange({ max: value })}
                                        />
                                        <Typography variant="montserrat412">
                                            %
                                        </Typography>
                                    </FunnelRetentionBody>
                                </FunnelRetentionContainer>
                            ),
                        }}
                        lineColor={theme.darkMode ? setup.colors.white : setup.colors.black}
                        lineCurveness={lineCurveness}
                        start="funnel-retention-start"
                        startAnchor={{ position: 'right' }}
                    />
                )}

                <Box
                    id={`funnel-retention-${(adding || connected) ? 'start' : 'end'}`}
                    sx={{
                        position: 'relative',
                        zIndex: 2000
                    }}>
                    <Box
                        sx={{
                            position: 'absolute',
                            right: -6,
                            top: -16
                        }}>
                        {adding && (
                            <IconButton label="Add retention" onClick={onAdd} >
                                <Add />
                            </IconButton>
                        )}
                        {connecting && (
                            <IconButton label="Connect retention" onClick={onConnect} >
                                <Point />
                            </IconButton>
                        )}
                    </Box>
                </Box>

            </Box>
            {connecting && (
                <Box
                    id="funnel-retention-anchor"
                    sx={{
                        bgcolor: 'red',
                        left: anchor.x,
                        position: 'fixed',
                        top: anchor.y
                    }} />
            )}
        </>
    );

}